/**
 * Funciones de utilidad para el manejo de fechas
 */

/**
 * Formatea una fecha para mostrarla en la interfaz
 * @param dateString - Cadena de fecha en varios posibles formatos
 * @returns Fecha formateada para mostrar
 */
export const formatDate = (dateString: string | null | undefined): string => {
  try {
    // Verificar si la fecha es válida
    if (!dateString) {
      return 'Fecha no disponible';
    }

    // Si ya está formateada como quisiéramos mostrarla, devolver como está
    if (/^\d{1,2} de [a-zA-Z]+ de \d{4}$/.test(dateString)) {
      return dateString;
    }

    // Intentar diferentes formatos de fecha
    let date;
    
    // Primero, probar con formato ISO (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const parts = dateString.split('T')[0].split('-');
      date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    }
    // Formato DD/MM/YYYY
    else if (/^\d{2}\/\d{2}\/\d{4}/.test(dateString)) {
      const [day, month, year] = dateString.split('/').map(Number);
      date = new Date(year, month - 1, day);
    }
    // Formato MM/DD/YYYY
    else if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(dateString)) {
      const parts = dateString.split('/');
      date = new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
    }
    // Si es un timestamp
    else if (!isNaN(Number(dateString))) {
      date = new Date(Number(dateString));
    }
    // Otros formatos
    else {
      // Intentar crear la fecha con el constructor estándar
      date = new Date(dateString);
    }

    // Verificar si la fecha resultante es válida
    if (isNaN(date.getTime())) {
      console.warn(`Formato de fecha inválido: ${dateString}`);
      return String(dateString); // Asegurar que se devuelve un string
    }

    return new Intl.DateTimeFormat('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  } catch (error) {
    console.error(`Error al formatear fecha: ${dateString}`, error);
    return String(dateString || 'Fecha no disponible'); // Asegurar que se devuelve un string
  }
};

/**
 * Verifica si una fecha está vencida
 * @param dateString - Cadena de fecha en varios posibles formatos
 * @returns true si la fecha está vencida, false en caso contrario
 */
export const isDateOverdue = (dateString: string | null | undefined): boolean => {
  try {
    if (!dateString) return false;
    
    // Intentar parsear la fecha
    const formattedDate = formatDate(dateString);
    if (formattedDate === String(dateString)) {
      // No se pudo parsear, asumir que no está vencida
      return false;
    }
    
    // Obtener la fecha actual (sin hora)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Intentar diferentes formatos para convertir a fecha
    let dueDate;
    
    // Formato ISO
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const parts = dateString.split('T')[0].split('-');
      dueDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    }
    // Formato DD/MM/YYYY
    else if (/^\d{2}\/\d{2}\/\d{4}/.test(dateString)) {
      const [day, month, year] = dateString.split('/').map(Number);
      dueDate = new Date(year, month - 1, day);
    }
    // Otros formatos
    else {
      dueDate = new Date(dateString);
    }
    
    dueDate.setHours(0, 0, 0, 0);
    
    return dueDate < today;
  } catch (error) {
    console.error(`Error verificando si la fecha está vencida: ${dateString}`, error);
    return false;
  }
};
